﻿import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useParams, Routes, Route, Navigate } from "react-router-dom";
import { CheckService } from "services/CheckService";
import { Breadcrumbs, HorizontalLinkGroup } from "components/navigation";
import { PageRestricted } from 'pages/errors/page-restricted';
import { ShowHide } from "../../../components/layout";
import { AlertConfirm, NotificationInline, NotificationToaster } from "components/notifications";
import { ButtonSave } from 'components/buttons'
import { FormCheckbox } from 'components/form-fields'; 
import { Tooltip } from 'components/tooltip';
import { CheckSummary } from "./shared/check-summary";
import { CheckFullResults } from "./shared/full-results/CheckFullResults";
import { DateToLocal } from "components/dates";
import { CheckDefectSummary } from "./defects";
import { Intent } from '@blueprintjs/core';
import classNames from "classnames";
import { TemplateSubCategories } from "../constants";
import { InstantReportButton } from "components/reporting";
import { LoadScript } from "@react-google-maps/api";
import { useQuery } from "hooks/useQuery";

export function CompletedCheck(props) {
    const { id } = useParams();
    const query = useQuery();
    const area = query.get("area");
    const requiredAction = "Checks:View";
    const canAccessPage = UserHasAccess(requiredAction);

    const [initialPageLoad, setInitialPageLoad] = useState(true);
    const [loadingData, setLoadingData] = useState(true);
    const [saving, setSaving] = useState(false);
    const [check, setCheck] = useState({});
    const [defects, setDefects] = useState([]);
    const [permissionDenied, setPermissionDenied] = useState(false);
    const [signOffRequired, setSignOffRequired] = useState(false);
    const [hasOutstandingDefects, setHasOutstandingDefects] = useState(false);
    const [templateLayout, setTemplateLayout] = useState({});
    const [template, setTemplate] = useState({});
    const [showCheckSignoffConfirm, setShowCheckSignoffConfirm] = useState(false);
    const [hasAgreedVehicleIsRoadworthy, setHasAgreedVehicleIsRoadworthy] = useState(false);
    const [reportGenerationId, setReportGenerationId] = useState(null);

    const routes = [
        { location: `/checks/${id}/details`, title: "Details" },
        { location: `/checks/${id}/results`, title: "Full Check Results" }
    ];
    
    const [breadcrumbs, setBreadcrumbs] = useState([]);
    const route = useSelector(state => state.route.route);
    const title = useSelector(state => state.route.title);
    const routeId = useSelector(state => state.route.id);

    useEffect(() => {
        if (area && area === "fleet") {
            setBreadcrumbs([{ location: '/', title: "Home" }, { location: '/fleet/overview', title: "Fleet Overview" }, { title: "Profile" }]);
        }
    }, [area]);

    useEffect(() => {
        if (area) {
            return;
        }
        var clonedBreadcrumbs = [...props.breadcrumbs];

        if (routeId != null && route != null) {
            clonedBreadcrumbs[0].location = `/${route}/${routeId}/checks`;
            clonedBreadcrumbs[0].title = title;
        } else {
            clonedBreadcrumbs[0] = { title: "Completed Checks", location: `/checks` };
        }

        setBreadcrumbs(clonedBreadcrumbs);
    }, [route, routeId, title, check?.templateName]);

    useEffect(() => {
        if (!canAccessPage) {
            return;
        }

        loadCompletedCheck();

    }, [id, canAccessPage]);

    async function loadCompletedCheck() {
        setLoadingData(true);

        try {
            const getCheckResponse = await CheckService.getCheck(id);
            const flatDefects = extractDefects(getCheckResponse.answers);

            setCheck(getCheckResponse);
            setDefects(flatDefects);
            setHasOutstandingDefects(flatDefects.some(defect => !defect.signOffDate));
            setSignOffRequired(getCheckResponse.requireManagerSignOff && !getCheckResponse.managerSignOff?.signOffDate);

            const getTemplate = await CheckService.getTemplate(getCheckResponse.templateId);
            setTemplate(getTemplate);

            const getTemplateLayoutResponse = await CheckService.getTemplateLayout(getCheckResponse.templateId);
            setTemplateLayout(getTemplateLayoutResponse);

        } catch (error) {
            handleError(error);
        } finally {
            setLoadingData(false);
            setInitialPageLoad(false);
        }
    };

    async function reloadCheckOnly() {
        const updatedCheck = await CheckService.getCheck(check.id);
        setCheck(updatedCheck);
    }

    function handleError(error) {
        const noAccessCode = 403;

        if (error.response.status === noAccessCode) {
            setPermissionDenied(true);
        }
    }

    function defectUpdated(updatedDefect) {
        setDefects((prevDefects) => {
            const updated = prevDefects.map(d =>
                d.defectId === updatedDefect.defectId ? { ...d, ...updatedDefect } : d
            );

            const hasUnsignedOff = updated.some(d => !d.signOffDate);
            setHasOutstandingDefects(hasUnsignedOff);

            return updated;
        });

        reloadCheckOnly();
    }

    function signOffCheck() {
        setSaving(true);
        setShowCheckSignoffConfirm(false)
        CheckService.signOffCheck(check.id).then(() => {
            NotificationToaster.show(Intent.SUCCESS, "Check signed off successfully.");
            loadCompletedCheck();
        })
            .catch((error) => {
                NotificationToaster.show(Intent.DANGER, "Failed to sign off check. Please try again later.");
            })
            .finally(() => {
                setSaving(false);
            });
    }

    function extractDefects(answers) {
        const result = [];

        for (const answer of answers) {
            if (answer.$answerType === 'DefectCapture' && Array.isArray(answer.defects)) {
                for (const defect of answer.defects) {
                    result.push({
                        questionId: answer.questionId,
                        ...defect,
                    });
                }
            }

            if (answer.$answerType === 'Repeatable' && Array.isArray(answer.answers)) {
                result.push(...extractDefects(answer.answers));
            }
        }

        return result;
    }

    function generateReport() {
        CheckService.generateCheckReport(id)
            .then(
                (response) => {
                    setReportGenerationId(response);
                },
                () => {
                    setReportGenerationId(null);
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Failed to generate report. Please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Failed to generate report. Please try again."
                );
            });
    }

    const ButtonRow = () => (
        <div className="inline-items inline-items-space spacer-bottom">
            <div className="button-row">
                <UserAccess
                    perform={["Checks:SignOff"]}
                    yes={() => (
                        <Tooltip content={hasOutstandingDefects ? "All defects must be signed off before the check can be signed off" : ""}>
                            <div>
                                <ButtonSave
                                    intent="primary"
                                    text="SIGN OFF CHECK"
                                    id="button-check-sign-off"
                                    loading={loadingData}
                                    onClick={() => setShowCheckSignoffConfirm(true)}
                                    icon="tick-circle"
                                    disabled={saving}
                                    simpleDisabled={hasOutstandingDefects || !signOffRequired || loadingData}
                                />
                            </div>
                        </Tooltip>

                    )}
                />
                <InstantReportButton
                    icon={<i className="fa fa-file-pdf-o"></i>}
                    key={"check-report"}
                    text={"Report"}
                    onReportGenerate={generateReport}
                    onReportDownloadComplete={() => setReportGenerationId(null)}
                    buttonDisabled={!!reportGenerationId}
                    reportId={reportGenerationId}
                />
            </div>
        </div>);

    function SignOffNotice({ subCategory }) {

        const text = subCategory === TemplateSubCategories.WALKAROUNDCHECK ? (<div><p>I consider that the defects have been rectified satisfactorily and this vehicle is now in a safe and roadworthy condition.</p>
                <p><strong>Note:</strong> It is always the reponsibility of the operator that the vehicle is in a roadworthy condition before being used on the road.</p></div>) : (<p>I consider that the check has been completed satisfactorily.</p>)        

        return (
            <FormCheckbox
                checked={hasAgreedVehicleIsRoadworthy}
                onChange={(e) => setHasAgreedVehicleIsRoadworthy(e.target.checked)}
                loading={loadingData}
                headingText={text}
            />                                        
        );
    }

    return (<UserAccess
        perform={[requiredAction]}
        yes={() =>
            <LoadScript googleMapsApiKey={window.env.GOOGLEMAPSAPIKEY}>
                <ShowHide
                    evaluator={permissionDenied}
                    show={
                        <div className="row">
                            <Breadcrumbs items={breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="You do not have permission to view this check"
                                intent="danger"
                            ></NotificationInline>
                        </div>
                    }
                    hide={
                        <div className="row">
                            <Breadcrumbs items={breadcrumbs} />
                            <h1>{check.templateName}</h1>
                            <NotificationInline
                                allowClose={false}
                                show={hasOutstandingDefects}
                                text="This Check still has defects to be signed off before the Check itself can be signed off"
                                intent="warning"                            
                            ></NotificationInline>
                            <NotificationInline
                                allowClose={false}
                                show={!!check.managerSignOff?.signOffDate}
                                text={<Fragment>This check report was signed off by {check.managerSignOff?.manager?.username} on <DateToLocal format='DD/MM/YYYY HH:mm'>{check.managerSignOff?.signOffDate}</DateToLocal></Fragment>}
                                intent="success"
                            ></NotificationInline>                       
                            <div className=" spacer-bottom-small spacer-top">
                                <ButtonRow />
                            </div>
                            <div className={classNames({ "bp6-skeleton": loadingData })} style={initialPageLoad ? { minHeight: "600px" } : {}}>

                                <HorizontalLinkGroup links={routes}></HorizontalLinkGroup>
                                <Routes>
                                    <Route path="details" element={<><CheckSummary loading={loadingData} check={check} defects={defects} template={template} />
                                        {defects.length > 0 && Object.keys(templateLayout).length > 0 &&
                                            defects.map((defect, index) => (
                                                <CheckDefectSummary
                                                    key={`defect-${defect.id}`}
                                                    defect={defect}
                                                    defectNumber={index + 1}
                                                    templateLayout={templateLayout}
                                                    loading={loadingData}
                                                    onDefectSignedOff={defectUpdated}
                                                    checkId={check.id}
                                                />
                                            ))}</>} />
                                    <Route path="results" element={<>{Object.keys(templateLayout).length > 0 && Object.keys(template).length > 0 && Object.keys(check).length > 0 && (
                                        <CheckFullResults check={check} templateLayout={templateLayout} />)}</>} />
                                    <Route path="" element={<Navigate to={`/checks/${id}/details`} replace />} />
                                </Routes>
                            </div>
                            <AlertConfirm
                                title="Sign Off Check"
                                isOpen={showCheckSignoffConfirm}
                                onConfirm={signOffCheck}
                                disabled={saving || !hasAgreedVehicleIsRoadworthy}
                                onCancel={() => setShowCheckSignoffConfirm(false)}
                                confirmButtonIntent="primary"
                                confirmButtonText="SIGN OFF CHECK">
                                <SignOffNotice subCategory={template.subCategory} />
                            </AlertConfirm>
                        </div>
                    }
                />
            </LoadScript>
        }
        no={() => (
            <PageRestricted />
        )}
    />);
}